import PartiesIcon from "@mui/icons-material/People";
import { Box } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCase, useCaseSubmit } from "../../context/CaseContext.tsx";
import {
  ConditionType,
  LOWEST_MINIMUM_COMPENSATION,
  PartyAgreements,
} from "../../shared/Agreements.ts";
import { AgreementsOfParty } from "../agreements/AgreementsOfParty.tsx";
import CenteredMessageBox from "../general/CenteredMessageBox.tsx";
import { GuideButton } from "../guide/GuideButton.tsx";
import { GuideMarkers } from "../guide/GuideContext.tsx";
import { useTheme } from "../../context/ThemeContext";

export const AgreementsPage = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const submit = useCaseSubmit();
  const navigate = useNavigate();
  const { isSaving } = useCase();

  const { id: caseId, parties = [], agreements = [] } = useCase().getCase();
  const clientParties = parties.filter(({ client }) => client);

  const [clientAgreements, setClientAgreements] = useState<PartyAgreements[]>(
    clientParties.map(({ id }) => {
      return (
        agreements.find(({ partyId }) => partyId === id) ?? {
          partyId: id,
          lawFirmCfa: {
            active: false,
            successFee: {
              conditionType: ConditionType.minimumCompensationRecovered,
              step: {
                successFeePercentage: 0,
                triggerAmount: LOWEST_MINIMUM_COMPENSATION,
              },
            },
            includedDisbursements: [],
            successFeeRecoveryPercentage: 0,
          },
          barristerCfa: {
            active: false,
            successFee: {
              conditionType: ConditionType.minimumCompensationRecovered,
              step: {
                successFeePercentage: 0,
                triggerAmount: LOWEST_MINIMUM_COMPENSATION,
              },
            },
            engagedBy: "party",
            successFeeRecoveryPercentage: 0,
          },
          afterTheEventInsurance: { active: false },
          thirdPartyFunding: { active: false },
        }
      );
    })
  );

  if (clientAgreements.length === 0) {
    return (
      <CenteredMessageBox
        message={t("agreement.noClients")}
        action={{
          label: t("agreement.goToParties"),
          icon: <PartiesIcon />,
          onClick: () => navigate(`/cases/${caseId}/configuration/parties`),
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <GuideButton />
      </Box>
      {clientAgreements.map((partyAgreements, index) => (
        // the guide marks what the agreements have in common on the first
        <GuideMarkers key={index} shown={index === 0}>
          <AgreementsOfParty
            agreements={partyAgreements}
            saveOngoing={isSaving}
            onUpdate={(newAgreements) => {
              setClientAgreements((prev) =>
                prev.map((agreement, agrementIndex) =>
                  agrementIndex === index ? newAgreements : agreement
                )
              );
            }}
            onSave={async (newAgreements) => {
              await submit(
                { partyAgreements: JSON.stringify(newAgreements) },
                { method: "put" }
              );
              setClientAgreements((prev) =>
                prev.map((agreement, agrementIndex) =>
                  agrementIndex === index ? newAgreements : agreement
                )
              );
            }}
          />
        </GuideMarkers>
      ))}
    </Box>
  );
};
